import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Grid, TableRow, TableCell,
  DialogContent, DialogActions, TextField, Alert, Tooltip,
  TablePagination, MenuItem, Select, FormControl, InputLabel, InputAdornment,
  useTheme, Chip, Divider, CircularProgress
} from '@mui/material';
import {
  Search, Visibility, Phone, Person, LocationOn, Storefront,
  Email, CreditCard, CalendarToday, Download
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassPaper, GlassTable, GlassDialog,
  GradientButton, EmptyState, ShimmerTableRows
} from './ui';
import { glass } from './ui/glassTokens';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'rejected', label: 'Rejected' },
];

function LibraryLeadsManagement({ apiBase = '/admin' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Detail dialog (join_request)
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editFollowUp, setEditFollowUp] = useState('');
  const [editStatus, setEditStatus] = useState('');

  // Detail dialog (website_plan purchase — read-only)
  const [purchaseDetailOpen, setPurchaseDetailOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchLeads();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const res = await api.get(`${apiBase}/library-leads?${params.toString()}`);
      setLeads(res.data?.data?.leads || []);
      setTotal(res.data?.data?.total || 0);
    } catch (err) {
      setError('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (lead) => {
    setSelectedLead(lead);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
    setEditFollowUp(lead.follow_up_date ? lead.follow_up_date.split('T')[0] : '');
    setDetailOpen(true);
  };

  const handleUpdateLead = async () => {
    try {
      await api.put(`${apiBase}/library-leads/${selectedLead.id}`, {
        status: editStatus,
        notes: editNotes,
        follow_up_date: editFollowUp || null,
      });
      setSuccess('Request updated successfully');
      setDetailOpen(false);
      fetchLeads();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update request');
    }
  };

  const handleOpenPurchaseDetail = (row) => {
    setSelectedPurchase(row);
    setPurchaseDetailOpen(true);
  };

  const handleDownloadInvoice = async (row) => {
    try {
      setDownloadingId(row.id);
      const res = await api.get(`${apiBase}/library-leads/${row.id}/invoice`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (row.payment?.library_name || row.name || 'library').replace(/[^\w-]+/g, '_');
      a.download = `invoice-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`${apiBase}/library-leads/${leadId}`, { status: newStatus });
      setSuccess('Status updated');
      fetchLeads();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSelect-select': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiInputBase-input': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
  };

  const paginationSx = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '& .MuiSelect-select': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' },
    '& .MuiSvgIcon-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
    '& .MuiButtonBase-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
  };

  return (
    <Box>
      <SectionHeader
        eyebrow="Libraries"
        title="Library Join Requests"
        description="People who requested to list their library via the marketing page"
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, phone, or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
              Total: <strong style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>{total}</strong> requests
            </Typography>
          </Grid>
        </Grid>
      </GlassPaper>

      {/* Table */}
      <GlassTable
        columns={[
          { label: 'Name' },
          { label: 'Phone' },
          { label: 'City' },
          { label: 'Status' },
          { label: 'Date' },
          { label: 'Payment' },
          { label: 'Actions', align: 'center' },
        ]}
      >
        {loading ? (
          <ShimmerTableRows count={5} cols={7} />
        ) : leads.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<Storefront />}
                iconIndex={1}
                title="No requests yet"
                description="Library join requests from the marketing page will appear here."
              />
            </TableCell>
          </TableRow>
        ) : leads.map((row) => {
          const isPurchase = row.entry_type === 'website_plan';
          const mutedColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)';
          const dimColor   = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)';
          const boldColor  = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)';

          return (
            <TableRow key={row.id}>
              {/* Name */}
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Typography variant="body2" fontWeight="600" sx={{ color: boldColor }}>
                    {row.name || 'N/A'}
                  </Typography>
                  {isPurchase && (
                    <Chip
                      label="Website plan"
                      size="small"
                      color="success"
                      sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
              </TableCell>

              {/* Phone */}
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title={`Call ${row.phone}`}>
                    <IconButton size="small" href={`tel:${row.phone}`}
                      sx={{ color: dimColor }}>
                      <Phone fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ color: mutedColor }}>
                    {row.phone}
                  </Typography>
                </Box>
              </TableCell>

              {/* City / Library name */}
              <TableCell>
                {isPurchase ? (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Storefront fontSize="small" sx={{ color: dimColor }} />
                    <Typography variant="body2" sx={{ color: mutedColor }}>
                      {row.city || '-'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: mutedColor }}>
                    {row.city || '-'}
                  </Typography>
                )}
              </TableCell>

              {/* Status */}
              <TableCell>
                {isPurchase ? (
                  <Chip
                    label="Paid"
                    size="small"
                    sx={{
                      background: isDark ? 'rgba(46,160,67,0.2)' : 'rgba(46,160,67,0.12)',
                      color: isDark ? '#4ade80' : '#15803d',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      border: `1px solid ${isDark ? 'rgba(74,222,128,0.3)' : 'rgba(21,128,61,0.3)'}`,
                    }}
                  />
                ) : (
                  <FormControl size="small" sx={{ minWidth: 130, ...selectSx }}>
                    <Select
                      value={row.status}
                      onChange={(e) => handleStatusChange(row.id, e.target.value)}
                      size="small"
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </TableCell>

              {/* Date */}
              <TableCell sx={{ fontSize: '0.8rem', color: dimColor }}>
                {formatDate(row.created_at)}
              </TableCell>

              {/* Payment */}
              <TableCell>
                {isPurchase && row.payment ? (
                  <Box>
                    <Typography variant="body2" fontWeight="600" sx={{ color: boldColor, lineHeight: 1.3 }}>
                      {row.payment.plan_name || 'Website plan'} · {row.payment.amount_display}
                    </Typography>
                    <Typography variant="caption" sx={{ color: dimColor, display: 'block', mt: 0.25 }}>
                      Paid {formatDate(row.payment.paid_at)}
                    </Typography>
                    {row.payment.razorpay_payment_id && (
                      <Tooltip title={row.payment.razorpay_payment_id}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.35)',
                            fontFamily: 'monospace',
                            fontSize: '0.65rem',
                            cursor: 'default',
                            maxWidth: 140,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.payment.razorpay_payment_id}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: dimColor }}>—</Typography>
                )}
              </TableCell>

              {/* Actions */}
              <TableCell align="center">
                {isPurchase ? (
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.25}>
                    <Tooltip title="Download Invoice">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadInvoice(row)}
                          disabled={downloadingId === row.id}
                          sx={{ color: dimColor }}
                        >
                          {downloadingId === row.id
                            ? <CircularProgress size={16} sx={{ color: dimColor }} />
                            : <Download fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="View Purchase Details">
                      <IconButton size="small" onClick={() => handleOpenPurchaseDetail(row)}
                        sx={{ color: dimColor }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleOpenDetail(row)}
                      sx={{ color: dimColor }}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </GlassTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
        sx={paginationSx}
      />

      {/* Purchase Detail Dialog (read-only) */}
      <GlassDialog open={purchaseDetailOpen} onClose={() => setPurchaseDetailOpen(false)} title="Website Plan Purchase" maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          {selectedPurchase && selectedPurchase.payment && (
            <Grid container spacing={2}>
              {/* Buyer info */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Buyer Information
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Person fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" fontWeight="600">{selectedPurchase.name || 'N/A'}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Phone fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      <a href={`tel:${selectedPurchase.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{selectedPurchase.phone}</a>
                    </Typography>
                  </Box>
                  {selectedPurchase.payment.email && (
                    <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                      <Email fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                      <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                        {selectedPurchase.payment.email}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Storefront fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      {selectedPurchase.payment.library_name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)' }} />
              </Grid>

              {/* Plan info */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Plan &amp; Payment
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Plan</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedPurchase.payment.plan_name || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Billing Period</Typography>
                      <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>{selectedPurchase.payment.billing_period || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Amount</Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CreditCard fontSize="small" sx={{ color: isDark ? 'rgba(74,222,128,0.7)' : '#15803d', fontSize: '0.9rem' }} />
                        <Typography variant="body2" fontWeight="700" sx={{ color: isDark ? '#4ade80' : '#15803d' }}>
                          {selectedPurchase.payment.amount_display}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Paid On</Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarToday fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)', fontSize: '0.85rem' }} />
                        <Typography variant="body2">{formatDate(selectedPurchase.payment.paid_at)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)' }} />
              </Grid>

              {/* Razorpay IDs */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Razorpay Reference
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)', display: 'block', mb: 0.25 }}>Payment ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.8)' }}>
                    {selectedPurchase.payment.razorpay_payment_id || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)', display: 'block', mt: 1, mb: 0.25 }}>Order ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.8)' }}>
                    {selectedPurchase.payment.razorpay_order_id || '—'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={() => setPurchaseDetailOpen(false)}>Close</GradientButton>
          <GradientButton
            variant="primary"
            onClick={() => selectedPurchase && handleDownloadInvoice(selectedPurchase)}
            disabled={!selectedPurchase || downloadingId === selectedPurchase?.id}
            startIcon={downloadingId === selectedPurchase?.id ? <CircularProgress size={16} color="inherit" /> : <Download />}
          >
            Download Invoice
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* Detail Dialog */}
      <GlassDialog open={detailOpen} onClose={() => setDetailOpen(false)} title="Join Request" maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          {selectedLead && (
            <Grid container spacing={2}>
              {/* Contact Info */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Contact Information
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Person fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" fontWeight="600">{selectedLead.name || 'N/A'}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Phone fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      <a href={`tel:${selectedLead.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{selectedLead.phone}</a>
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      {selectedLead.city || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)' }} />
              </Grid>

              {/* Editable Fields */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    label="Status"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Follow-up Date"
                  type="date"
                  value={editFollowUp}
                  onChange={(e) => setEditFollowUp(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this request..."
                  sx={inputSx}
                />
              </Grid>

              {/* Metadata */}
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                  Submitted: {formatDate(selectedLead.created_at)}
                  {selectedLead.contacted_at && ` | Contacted: ${formatDate(selectedLead.contacted_at)}`}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={() => setDetailOpen(false)}>Cancel</GradientButton>
          <GradientButton variant="primary" onClick={handleUpdateLead}>Update</GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}

export default LibraryLeadsManagement;
